/** @file overlayFrame: the overlay camera's device-pixel ortho bounds and the puppet quad in its frame. */
import { targetScreenOf } from './targetScreenOf';
import type { ViewportLayout } from './viewportLayout';

/** Ortho bounds (scene units, around the camera) and the puppet quad's centre and size in camera-local units. */
export type OverlayFrame = {
  ortho: { left: number; right: number; top: number; bottom: number };
  quad: { x: number; y: number; width: number; height: number };
};

/**
 * The puppet camera maps target pixel `(sx, sy)` to camera-local `((sx - originX) / ppu, (originY - sy) / ppu)` (its
 * ortho bounds, `orbitCamera`). The overlay camera shares its position and turn and shows the whole canvas at
 * `ppu x scale` device pixels per unit so that target pixel `(sx, sy)` lands on device pixel `origin + scale (sx, sy)`
 * of `targetScreenOf` (the one target -> screen map). The quad covers the puppet camera's rectangle.
 */
export function overlayFrame(layout: ViewportLayout, originX: number, originY: number, ppu: number): OverlayFrame {
  const { fit, device: [w, h] } = layout;
  const { origin: [ox, oy], scale } = targetScreenOf(layout);
  const k = ppu * scale;
  const x0 = ox + originX * scale;
  const y0 = oy + originY * scale;
  return {
    ortho: { left: -x0 / k, right: (w - x0) / k, top: y0 / k, bottom: -(h - y0) / k },
    quad: { x: (fit.width / 2 - originX) / ppu, y: (originY - fit.height / 2) / ppu, width: fit.width / ppu, height: fit.height / ppu },
  };
}
